import { Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

// --- Rollup bitmask helpers ---
// Postgres ROLLUP grouping bitmask: bit (totalDims-1-i) set means dim Di is rolled up.
// A :with_id dim counts as 2 SQL columns. We use this to find the correct rollup level
// regardless of array ordering in the response.

export function totalDimsFor(rollups: AnalyticsResponseRollupCollection): number {
  return rollups.analyticsResponse.dimensionHeaders.length;
}

export function groupingValueFor(totalDims: number, preservedDimIndices: ReadonlySet<number>): number {
  let value = 0;
  for (let i = 0; i < totalDims; i++) {
    if (!preservedDimIndices.has(i)) value |= 1 << (totalDims - 1 - i);
  }
  return value;
}

export function findRollupByGroupingValue(rollups: AnalyticsResponseRollupCollection, value: number) {
  return rollups.groupings.find((g) => g.current[0]?.grouping === value);
}

// Grand-total metric value (all dims rolled up).
export function readGrandTotal(rollups: AnalyticsResponseRollupCollection, metric: Metrics): number | null {
  const totalDims = totalDimsFor(rollups);
  const grandTotalTarget = groupingValueFor(totalDims, new Set());
  const total = findRollupByGroupingValue(rollups, grandTotalTarget);
  if (!total) return null;
  return total.metrics.getCurrentSeries(metric)[0] ?? null;
}

// Same as readGrandTotal but defaulting null to 0 -- for count metrics that should never be null in UI.
export function readGrandTotalOrZero(rollups: AnalyticsResponseRollupCollection, metric: Metrics): number {
  return readGrandTotal(rollups, metric) ?? 0;
}

// Aggregate a metric from grouping-0 detail rows, grouped by one dimension and pivoted by another.
// Useful when ROLLUP doesn't produce a needed cross-section (e.g., company × type with group rolled up).
// Returns Map<groupByValue, Map<pivotValue, sum>>.
export function sumDetailRowsByDimensions(
  rollups: AnalyticsResponseRollupCollection,
  groupByDimension: string,
  pivotDimension: string,
  metric: string
): Map<string, Map<string, number>> {
  const { analyticsResponse } = rollups;
  const groupByIdx = analyticsResponse.findDimensionIndex(groupByDimension);
  const pivotIdx = analyticsResponse.findDimensionIndex(pivotDimension);
  const metricIdx = analyticsResponse.findMetricIndex(metric);

  if (groupByIdx < 0 || pivotIdx < 0 || metricIdx < 0) return new Map();

  const result = new Map<string, Map<string, number>>();

  for (const row of analyticsResponse.responseData) {
    if ((row.grouping ?? 0) !== 0 || (row.datesIndex ?? 0) !== 0) continue;

    const groupKey = String(row.dimensionValues[groupByIdx]?.value ?? '');
    const pivotKey = String(row.dimensionValues[pivotIdx]?.value ?? '');
    const value = Number(row.metricValues[metricIdx]?.value ?? 0);

    if (!groupKey || !pivotKey) continue;

    let group = result.get(groupKey);
    if (!group) {
      group = new Map();
      result.set(groupKey, group);
    }
    group.set(pivotKey, (group.get(pivotKey) ?? 0) + value);
  }

  return result;
}

// Sum metric values grouped by dimension value. Returns a Map<dimensionValue, total>.
export function sumByDimensionValue(
  names: ReadonlyArray<string | null | undefined>,
  values: ReadonlyArray<number | null | undefined>
): Map<string, number> {
  const result = new Map<string, number>();
  names.forEach((name, i) => {
    const key = String(name ?? '');
    if (!key) return;
    result.set(key, (result.get(key) ?? 0) + (values[i] ?? 0));
  });
  return result;
}

// Zip dimension names + counts, drop empty / 'None' / zero-count rows, optionally remap display names,
// optionally sort by count desc. Used by the per-metric breakdown charts.
export function buildBreakdownRows(
  names: ReadonlyArray<string | null | undefined>,
  counts: ReadonlyArray<number | null | undefined>,
  options: { displayNameMap?: Record<string, string>; lowercase?: boolean; sortDesc?: boolean } = {}
): Array<{ name: string; count: number }> {
  const { displayNameMap, lowercase, sortDesc } = options;
  const rows = names
    .map((raw, i) => {
      const key = String(raw ?? '');
      const display = displayNameMap?.[key] ?? key;
      return { name: lowercase ? display.toLowerCase() : display, count: counts[i] ?? 0 };
    })
    .filter((r) => r.name && r.name.toLowerCase() !== 'none' && r.count > 0);
  if (sortDesc) rows.sort((a, b) => b.count - a.count);
  return rows;
}
